// Shared validation + wire-shaping for tenant academic config (PRD-38 /
// ADR-0032). Pure — no React, no network. The React state lives in
// `@hooks/useAcademicConfig`, the UI in `@components/AcademicConfigFields`
// and `features/schools/components/AcademicOverrideFields`.
//
// Two shapes, because the inheritance model has two levels:
//   • `AcademicConfigValues`   — Institution. All three REQUIRED (non-null
//     columns; nothing above an Institution to inherit from).
//   • `AcademicOverrideValues` — School. All three NULLABLE, where null means
//     "inherit from the institution".

import { z } from "zod"

import {
  GRADE_SCALES,
  MAX_PERIODS_PER_CYCLE,
  MIN_PERIODS_PER_CYCLE,
  PERIOD_TYPES,
  type GradeScale,
  type PeriodType,
} from "@/constants/academic"

export interface AcademicConfigValues {
  period_type: PeriodType
  /** May be NaN while the admin has the number input cleared mid-edit. */
  periods_per_cycle: number
  grade_scale: GradeScale
}

export interface AcademicOverrideValues {
  period_type: PeriodType | null
  /** null = inherit. NaN is normalized to null before validation. */
  periods_per_cycle: number | null
  grade_scale: GradeScale | null
}

// Mirrors the API's `periods_per_cycle_out_of_range` bounds so an out-of-range
// value is caught inline rather than round-tripping to a 422. The messages are
// error CODES; the UI maps them through `common:academic.error.*`.
const periodsPerCycleSchema = z
  .number({ invalid_type_error: "required", required_error: "required" })
  .int("invalid")
  .min(MIN_PERIODS_PER_CYCLE, "out_of_range")
  .max(MAX_PERIODS_PER_CYCLE, "out_of_range")

export const academicConfigSchema = z.object({
  period_type: z.enum(PERIOD_TYPES),
  periods_per_cycle: periodsPerCycleSchema,
  grade_scale: z.enum(GRADE_SCALES),
})

export const academicOverrideSchema = z.object({
  period_type: z.enum(PERIOD_TYPES).nullable(),
  periods_per_cycle: periodsPerCycleSchema.nullable(),
  grade_scale: z.enum(GRADE_SCALES).nullable(),
})

/**
 * An empty number input means "inherit", so NaN collapses to null before the
 * override is validated or sent.
 */
export function normalizeOverride(values: AcademicOverrideValues): AcademicOverrideValues {
  return {
    ...values,
    periods_per_cycle:
      values.periods_per_cycle === null || Number.isNaN(values.periods_per_cycle)
        ? null
        : values.periods_per_cycle,
  }
}

/**
 * The School create/update academic payload — ALWAYS all three keys, with an
 * EXPLICIT null for every field left on "inherit".
 *
 * This is the load-bearing detail of the whole override UX (PRD-38 §FR-11).
 * The backend PATCH applies `exclude_unset=True`, so the two cases are NOT
 * interchangeable:
 *   • key ABSENT        → "don't touch this override" (a previously-set
 *                          override would silently survive a clear).
 *   • key PRESENT, null → "clear this override back to inherit".
 * Emitting all three unconditionally also gives per-field independence for
 * free: overriding only `grade_scale` sends null for the other two, which is
 * exactly "leave them inherited".
 */
export function toAcademicOverridePayload(values: AcademicOverrideValues): AcademicOverrideValues {
  return normalizeOverride(values)
}
